import {
  AppContext,
  BackgroundIcon,
  getHeaderValue,
  useUserMSPCheckHook,
  useIsAuthorisedHook,
  useActivityTracker,
  getBrowserType,
  fetchApi,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import {
  Popover,
  Avatar,
  ResourceItem,
  ResourceList,
  Card,
  DisplayText,
  Modal,
  Page,
  TextField,
  Banner,
  Button,
  ButtonGroup,
  Collapsible,
  Spinner
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { FormattedMessage } from 'react-intl';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import { cloneDeep } from 'lodash';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_USERS_ADD_ONS,
  INITIATE_SOS_CONNECT,
  AccountClient,
  RMMClient,
  savePersonalization
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';

const Modules = styled.div`
  padding: 5px 23px;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;
const MoveRight = styled.div`
  margin-left: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
`;
const ModulesIcons = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  margin-right: 18px;
  margin-left: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WrapModulesIcons = styled.div`
  position: relative;
  top: 2px;
`;
const UserHeaderModule = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 9px 0;
  margin-bottom: 10px;
`;
const ActivatorQrIcon = styled.div`
  background-color: #3c3b3d;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  justify-content: center;
  display: flex;
  align-items: center;
  position: relative;
  top: -14px;
  left: 22px;
`;
const UserListTextGap = styled.div`
  margin-left: 15px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 200px;
  cursor: pointer;
}
`;

const QRMessageWrapper = styled.div`
  display: flex;
  width: 394px;
  padding: 20px;
  gap: 20px;
  align-items: center;
  .scan-img {
    width: 100%;
    max-width: 84px;
  }

  .heading {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 5px;
  }

  .content {
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
  }
`;

const UserAppMenuNew = (props) => {
  const { flags } = props;
  const [popoverActive, setPopoverActive] = useState(false);
  const [isSplashTopSOSEnabled, setSplashTopSOSEnabled] = useState(false);
  const [qrMessage, setQrMessage] = useState(props.showQrMessage);
  const [showSplashtopSOS, setShowSplashtopSOS] = useState(false);
  const [splashtopSOSCode, setSplashtopSOSCode] = useState(null);
  const [openInstructions, setOpenInstructions] = useState(false);
  const isMSP = useUserMSPCheckHook();
  const isIntegrationsViewEnabled = useIsAuthorisedHook('remoteDesktop.allow');
  const isITProduct = useProduct(COMMON_CONSTANTS.IT);
  const appContext = useContext(AppContext);
  const { user, currentServicePortal } = appContext;
  const [showLoadingOnProgress, setShowLoadingOnProgress] = useState(false);
  const isRequesterITPortal =
    currentServicePortal === COMMON_CONSTANTS.REQUESTER;

  function isShowSpaceSwitch() {
    const userHasTwoRoles = user.roles.length > 1;
    if (isITProduct && userHasTwoRoles) {
      return true;
    }
    return false;
  }

  const SPLASHTOP_SOS_ID = '2002';
  const { loading: userAddOnLoading, data: userAddOnData } = useQuery(
    GET_USERS_ADD_ONS,
    {
      skip: !isIntegrationsViewEnabled || !(isMSP && popoverActive),
      client: AccountClient,
      variables: {
        addOnIds: [SPLASHTOP_SOS_ID]
      }
    }
  );
  const [sosQuickconnect, { loading: initiateLoading }] = useMutation(
    INITIATE_SOS_CONNECT,
    {
      client: RMMClient,
      variables: {
        sosCode: splashtopSOSCode
      },
      onCompleted: (res) => {
        if (res) {
          const sosConnectURL = res.initiateSosSession;
          window.open(sosConnectURL);
        }
        setShowSplashtopSOS(false);
        setSplashtopSOSCode(null);
      }
    }
  );
  useEffect(() => {
    if (userAddOnData) {
      const isSosEnabled =
        userAddOnData?.getAddOnStatusForLoggedInUser?.find(
          (s) => s.addOnId?.toString() === SPLASHTOP_SOS_ID
        )?.enabled || false;
      setSplashTopSOSEnabled(isSosEnabled);
    }
  }, [userAddOnData, userAddOnLoading]);
  useEffect(() => {
    if (props.showQrMessage) {
      const timer1 = setTimeout(() => setQrMessage(true), 1000);
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [props.showQrMessage]);

  useEffect(() => {
    if (qrMessage) {
      const resetQrMessage = setTimeout(() => setQrMessage(false), 3000); //clearing popup after 3s
      return () => {
        clearTimeout(resetQrMessage);
      };
    }
  }, [qrMessage]);

  const homeContext = useContext(HomeContext);
  const browser = homeContext.getBrowserType();
  const { conf } = props;
  const LogoutFunction = async () => {
    localStorage.removeItem('feedback');
    const requestOptions = {
      method: 'POST',
      headers: getHeaderValue(),
      credentials: 'include',
      redirect: 'follow'
    };

    try {
      const response = await fetchApi(
        `${props.conf.logoutRoot}/logout`,
        requestOptions
      );

      if (response?.redirected) {
        window.location.href = window.location.origin;
      } else if (response?.ok) {
        const logoutRes = await response?.json();
        window.location.href = logoutRes?.redirectURL || window.location.origin;
      }
      return;
    } catch (err) {
      console.log(err, 'err');
    }
  };
  const togglePopoverActive = useCallback(() => {
    if (
      homeContext.selectedModule === 'settings' &&
      homeContext.toggleSettingsOverlay
    ) {
      homeContext.toggleSettingsOverlay();
    }
    if (
      document.getElementsByClassName('RunningTimersPopup') &&
      document.getElementsByClassName('RunningTimersPopup').length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName('RunningTimersPopup'),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
    setPopoverActive((popoverActive) => !popoverActive);
  });

  const handleThemeChange = (val) => {
    appContext?.setDarkTheme(val);
    const currentUser = appContext?.user || {};
    const user = {
      firstName: currentUser.firstName,
      userId: currentUser.userId,
      email: currentUser.email,
      lastName: currentUser.lastName
    };
    const personalisedData = {
      name: 'Theme',
      category: 'ThemePersonalization',
      value: JSON.stringify({ darkTheme: val }),
      user: user
    };
    savePersonalization({
      variables: personalisedData,
      input: personalisedData
    });
  };
  const handleServicePortalChange = async () => {
    try {
      setShowLoadingOnProgress(true);
      let updatedServicePortal = COMMON_CONSTANTS.TECHNICIAN;

      if (currentServicePortal === COMMON_CONSTANTS.TECHNICIAN) {
        updatedServicePortal = COMMON_CONSTANTS.REQUESTER;
      } else {
        updatedServicePortal = COMMON_CONSTANTS.TECHNICIAN;
      }
      const personalisedData = {
        name: 'CurrentServicePortal',
        category: 'currentServicePortalPersonalization',
        value: updatedServicePortal,
        user: {
          firstName: user.firstName,
          userId: user.userId,
          email: user.email,
          lastName: user.lastName
        }
      };
      await savePersonalization({
        variables: personalisedData,
        input: personalisedData
      });
      localStorage.setItem('currentServicePortal', updatedServicePortal);
      window.location.replace(window.location.origin); // reset the current url and reload the page
      // window.location.reload();
      // setShowLoadingOnProgress(false);
    } catch (ex) {
      console.error(ex);
      setShowLoadingOnProgress(false);
    }
  };

  const ThemeIcon = () => {
    return appContext?.darkTheme ? (
      <div className="theme-toggle-btn dark-theme-btn fx center jctr">
        <p>
          <FormattedMessage id="darkmode.toggle.on" />
        </p>
        <div className="fx center jctr">
          <SVGIcon name="Moon" size="xs" color="var(--sops-white-to-white)" />
        </div>
      </div>
    ) : (
      <div className="theme-toggle-btn light-theme-btn fx center jctr">
        <div className="fx center jctr">
          <SVGIcon name="Sun" size="xs" color="var(--sops-white-to-white)" />
        </div>
        <p>
          <FormattedMessage id="darkmode.toggle.off" />
        </p>
      </div>
    );
  };

  const handleCreation = (mod) => {
    const currentAppState = homeContext?.userAppState || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState.appState);
    if (mod.name === 'splashtopsos') {
      setShowSplashtopSOS(true);
      return;
    }
    if (mod.name === 'serviceportal') {
      handleServicePortalChange();
      return;
    }
    let tab = {
      id: 'canny-tab',
      content: '',
      module: 'CANNY_FEEDBACK',
      tabId: 'canny-tab',
      state: {},
      data: {}
    };

    setPopoverActive(false);
    // updatedAppState.push(tab);
    if (mod.name === 'productonboard') {
      tab = updatedAppState.find((el) => el.id === 'productonboard-tab');
      if (!tab) {
        tab = {
          id: 'productonboard-tab',
          content: '',
          module: 'PRODUCTONBOARD',
          tabId: 'productonboard-tab',
          state: {},
          data: {},
          url: '/productonboard'
        };
        updatedAppState.push(tab);
      }
    } else if (mod.name === 'forum') {
      tab = updatedAppState.find((el) => el.id === 'Forum');
      if (!tab) {
        tab = {
          id: 'Forum',
          content: '',
          module: 'PRODUCT_FORUM',
          tabId: 'forum-tab',
          state: {},
          data: {},
          url: '/product/forum'
        };
        updatedAppState.push(tab);
      }
    } else if (mod.name === 'canny') {
      const tabIdx = updatedAppState.findIndex(
        (el) => el.module === 'CANNY_FEEDBACK'
      );
      tab.url = mod.url;
      if (mod.tabname) tab.state = { tabname: mod.tabname };
      if (tabIdx === -1) {
        updatedAppState.push(tab);
      } else {
        updatedAppState[tabIdx] = tab;
      }
    } else if (mod.name === 'help') {
      window.open(
        'https://support.superops.ai/en/',
        '_blank' // <- This is what makes it open in a new window.
      );
    } else if (mod.name === 'logout') {
      LogoutFunction();
      return;
    } else if (mod.name === 'darkmode') {
      handleThemeChange(!appContext?.darkTheme);
    } else if (mod.name === 'chat') {
      setPopoverActive(false);
      window.Intercom('show');
      return;
    } else if (mod.name === 'username') {
      tab = updatedAppState.find((el) => el.id === 'profile');
      if (!tab) {
        tab = {
          id: 'profile',
          content: getTabTemplate({ module: 'PROFILE' }),
          accessibilityLabel: 'My profile',
          module: 'PROFILE',
          tabId: 8,
          state: {},
          data: {},
          url: '/profile/detail/edit'
        };
        updatedAppState.push(tab);
      }
    } else if (mod === 'qr-scan') {
      let tabIdx = updatedAppState.findIndex(
        (el) => el.id === (isMSP ? 'onboard-tab' : 'profile')
      );
      tab = isMSP
        ? {
            id: 'onboard-tab',
            content: '',
            module: 'ONBOARD',
            tabId: 'onboard-tab',
            state: {
              tabname: 'qr-login'
            },
            data: {},
            url: '/onboard/profile'
          }
        : {
            id: 'profile',
            content: '',
            module: 'PROFILE',
            tabId: 8,
            state: {
              tabname: 'qr-login'
            },
            data: {},
            url: '/profile/detail/edit'
          };
      if (tabIdx === -1) {
        updatedAppState.push(tab);
      } else {
        updatedAppState[tabIdx] = {
          ...updatedAppState[tabIdx],
          state: {
            tabname: 'qr-login'
          },
          data: {},
          url: isMSP ? '/onboard/profile' : '/profile/detail/edit'
        };
      }
    }
    if (mod.name !== 'help') {
      homeContext.updateUserAppState({
        ...currentAppState,
        appState: updatedAppState,
        activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
      });
      homeContext.closeCreateNewOverlay(true);
      setTimeout(() => {
        homeContext.switchTabFocus(tab);
      }, 200);
    }
  };

  const loginUserList = {
    id: 'loggedInUserName',
    url: '/profile/detail/edit',
    name: 'username',
    label: `${user?.name}`,
    location: '',
    icon: (
      <Avatar
        name={user?.name}
        initials={user?.name.slice(0, 1).toUpperCase()}
      />
    ),
    extraicon: <SVGIcon name="QRCode" size="lg" />
  };

  const browserType = getBrowserType();
  const userMenuList = [
    {
      id: 'darkmode',
      url: null,
      name: 'darkmode',
      label: 'admin.usermenu.darkmode',
      color: '#EDFDE1',
      icon: <SVGIcon name="PaintingPalette" />,
      extraicon: <ThemeIcon />,
      isShow: browserType !== 'Apple Safari'
    },
    {
      id: 'splashtopsos',
      url: null,
      name: 'splashtopsos',
      label: 'splashtop.sos.connect',
      color: 'rgba(253, 233, 179, 1)',
      icon: <SVGIcon name="Connect" />,
      isShow: isSplashTopSOSEnabled
    },
    {
      id: 'serviceportal',
      url: null,
      name: 'serviceportal',
      label: isRequesterITPortal
        ? 'common.technician.portal'
        : 'common.service.portal',
      color: 'rgba(214, 250, 255, 1)', // TODO: DSA Colours
      icon: <SVGIcon name="UserPortal" />,
      isShow: isShowSpaceSwitch(),
      showLoadingOnProgress: true
    },
    {
      id: 'chat',
      url: null,
      name: 'chat',
      label: 'admin.chat.support',
      color: 'rgba(244, 230, 103, 0.2)',
      icon: <SVGIcon name="IntercomChat" />,
      isShow: flags.moveIntercom
    },
    {
      id: 'help',
      url: null,
      name: 'help',
      label: 'admin.usermenu.popup.helpcenter',
      color: 'rgba(244,103,137,20%)',
      icon: <SVGIcon name="Helpcenter" />,
      extraicon: <SVGIcon name="Redirect" color="#2901FE" />,
      isShow: isMSP && !isITProduct
    },
    {
      id: 'productonboard',
      url: null,
      name: 'productonboard',
      label: 'admin.usermenu.popup.product.onboarding',
      color: 'rgba(159, 214, 178, 20%)',
      icon: <SVGIcon name="ProductOnboarding" />,
      isShow: useIsAuthorisedHook('adminOnboarding.allow')
    },
    {
      id: 'canny',
      url: '/product/feedback?tab=feedback',
      name: 'canny',
      label: 'admin.usermenu.popup.feedback',
      color: 'rgba(117, 117, 226, 20%)',
      icon: <SVGIcon name="Feedback" size="lg" />,
      isShow: isMSP && !isITProduct,
      mixpanelEvent: 'Feedback Page - Click'
    },
    {
      id: 'releasenotes',
      url: '/product/feedback?tab=changelog',
      name: 'canny',
      label: 'admin.usermenu.popup.release.notes',
      color: 'rgba(176, 128, 232, 20%)',
      icon: <SVGIcon name="ReleaseNotes" />,
      isShow: isMSP && !isITProduct,
      mixpanelEvent: 'ReleaseNotes Page - Click'
    },
    {
      id: 'forum',
      url: '/product/feedback?tab=forum',
      // name: 'forum',
      name: 'canny',
      label: 'admin.usermenu.popup.forum',
      color: 'rgba(255, 66, 130, 20%)',
      icon: <SVGIcon name="Forum" />,
      isShow: isMSP && !isITProduct,
      mixpanelEvent: 'Forum Page - Click'
    },
    {
      id: 'logout',
      url: `${conf.loginRoot}/logout`,
      name: 'logout',
      label: 'admin.usermenu.popup.logout',
      color: 'rgba(0, 170, 255, 20%)',
      icon: <SVGIcon name="Logout" />,
      isShow: true
    }
  ];

  const activator = (
    <div onClick={togglePopoverActive} className="nav-group-activator">
      <div className="user-avator">
        <Avatar
          name={user?.name}
          initials={user?.name.slice(0, 1).toUpperCase()}
          size="small"
        />
        <ActivatorQrIcon>
          <SVGIcon name="QRCode" size="sm" color="#fff" />
        </ActivatorQrIcon>
      </div>
    </div>
  );
  const UserMenuText = ({ menulist }) => {
    return (
      <>
        <div className="superops-menu-title">
          <DisplayText weight="Regular" size="extraLarge">
            <FormattedMessage id={menulist.label} />
          </DisplayText>
        </div>
        {menulist.extraicon && <MoveRight>{menulist.extraicon}</MoveRight>}
      </>
    );
  };
  const LoginUserInfo = ({ menulist }) => {
    return (
      <UserHeaderModule>
        <Modules>
          <div>{menulist.icon}</div>
          <UserListTextGap onClick={handleCreation.bind(this, menulist)}>
            <DisplayText element="h2" weight="Medium" size="large">
              <div title={menulist.label} className="overflow-ellipses">
                <FormattedMessage id={menulist.label} />
              </div>
            </DisplayText>
          </UserListTextGap>
          <MoveRight
            onClick={() => {
              handleCreation('qr-scan');
            }}
          >
            {menulist.extraicon}
          </MoveRight>
        </Modules>
      </UserHeaderModule>
    );
  };
  const MenuListInfo = ({ menulist }) => {
    return (
      <Modules
        onClick={() => {
          menulist?.mixpanelEvent &&
            useActivityTracker({
              event: menulist.mixpanelEvent
            });
        }}
        className="full-width"
      >
        <ModulesIcons
          style={
            menulist.color
              ? {
                  background: menulist.color
                }
              : {}
          }
        >
          <WrapModulesIcons>{menulist.icon}</WrapModulesIcons>
        </ModulesIcons>
        <UserMenuText menulist={menulist} />
      </Modules>
    );
  };
  return (
    <>
      <Popover
        active={qrMessage}
        activator={activator}
        onClose={() => setQrMessage(false)}
        customClassName="qrMessage-popup"
      >
        <QRMessageWrapper>
          <div>
            {/* Illustration images hence not changed to SVG */}
            <BackgroundIcon
              url={`url(${envConfig.imageRoot}svg/qr-scan-menu.svg)`}
              style={{ width: '84px', height: '76px' }}
            />
          </div>
          <div>
            <p className="heading">
              <FormattedMessage id="appMenu.popover.accessQrHere" />
            </p>
            <p className="content">
              <FormattedMessage id="appMenu.popover.accessQrinProfile" />
            </p>
          </div>
        </QRMessageWrapper>
      </Popover>

      <Popover
        active={popoverActive}
        activator={activator}
        onClose={togglePopoverActive}
        preferredAlignment="left"
        fullHeight
        fullWidth
        customClassName="custom-onboard-popup"
        bgVariant="primary"
      >
        <Card>
          <LoginUserInfo menulist={loginUserList} />
          <ResourceList
            items={userMenuList}
            renderItem={(menulist, index) => {
              return menulist.id === 'productonboard' &&
                user?.role.roleId !== '1'
                ? null
                : menulist.isShow && (
                    <div className={index === 'logout' ? '' : 'mb5'}>
                      <ResourceItem
                        id={menulist.label}
                        key={menulist.label}
                        onClick={handleCreation.bind(this, menulist)}
                        gutter="xs"
                        disabled={
                          menulist.showLoadingOnProgress &&
                          showLoadingOnProgress
                        }
                      >
                        <div className="flex actr fx-sb">
                          <MenuListInfo menulist={menulist} />
                          {menulist.showLoadingOnProgress &&
                            showLoadingOnProgress && (
                              <Spinner variation="default" size="small" />
                            )}
                        </div>
                      </ResourceItem>
                    </div>
                  );
            }}
          />
        </Card>
      </Popover>
      <Modal open={showSplashtopSOS} title="" showCloseButton={false} noScroll>
        <Page
          title={<FormattedMessage id="splashtop.sos.quick.connect" />}
          contentHeight="80px"
          variant="secondary"
          divider
          primaryAction={
            <>
              <ButtonGroup spacing="loose">
                <Button
                  plainSecondary
                  onClick={() => {
                    setSplashtopSOSCode(null);
                    setShowSplashtopSOS(false);
                    setOpenInstructions(false);
                  }}
                >
                  <FormattedMessage id="common.cancel" />
                </Button>
                <Button
                  primary
                  onClick={() => sosQuickconnect()}
                  loading={initiateLoading}
                  disabled={splashtopSOSCode?.length !== 9}
                >
                  <FormattedMessage id="common.connect" />
                </Button>
              </ButtonGroup>
            </>
          }
        >
          <div className="p2040">
            <div className="mb-20">
              <div className="flex actr g5">
                <SVGIcon name="TerminalCode" />
                <FormattedMessage id="splashtop.sos.connection.code" />
              </div>
              <TextField
                value={splashtopSOSCode}
                onChange={(val) => {
                  const sosCodeWithHypen = val.replace('-', '');
                  setSplashtopSOSCode(sosCodeWithHypen);
                }}
                autoFocus
                focused
                placeholder="123456789"
                maxLength={9}
              ></TextField>
            </div>
            <div className="flex actr mb-20">
              <Banner status="info">
                <FormattedMessage id="splashtop.sos.connect.banner.info" />
              </Banner>
            </div>
            <div className="">
              <Button
                icon={<SVGIcon name="About" />}
                plain
                onClick={() => setOpenInstructions((prev) => !prev)}
              >
                <FormattedMessage id="common.view.instructions" />
              </Button>
            </div>
            <Collapsible open={openInstructions}>
              <FormattedMessage
                id="splashtop.sos.popup.instructions"
                values={{
                  br: <br />,
                  ol: (chunks) => <ol>{chunks}</ol>,
                  ul: (chunks) => <ul>{chunks}</ul>,
                  li: (chunks) => <li>{chunks}</li>,
                  strong: (chunks) => <strong>{chunks}</strong>,
                  em: (chunks) => <em>{chunks}</em>,
                  div: (chunks) => <div>{chunks}</div>,
                  b: (chunks) => <b>{chunks}</b>,
                  aWindows: (chunks) => (
                    <a href="https://my.splashtop.com/rmm/win" target="_blank">
                      {chunks}
                    </a>
                  ),
                  aMac: (chunks) => (
                    <a href="https://my.splashtop.com/rmm/mac" target="_blank">
                      {chunks}
                    </a>
                  ),
                  aSOSApp: (chunks) => (
                    <a
                      href="https://sos.splashtop.com/en/sos-download"
                      target="_blank"
                    >
                      {chunks}
                    </a>
                  )
                }}
              />
            </Collapsible>
          </div>
        </Page>
      </Modal>
    </>
  );
};

export default withLDConsumer()(UserAppMenuNew);
